import React, { useState, memo } from 'react';
import { ActivityIcon, BrainIcon, HeartIcon, SparklesIcon, ThermometerIcon, EyeIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export function Treatments() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  const specializations = [{
    id: 'master-tung',
    icon: ActivityIcon,
    title: 'Master Tung Acupuncture',
    color: 'blue',
    description: 'Highly effective specialized acupuncture system utilizing approximately 740 unique points not found in standard TCM meridian systems.',
    benefits: ['Rapid pain relief and immediate clinical results, often requiring fewer needles (typically 8 or less) than traditional acupuncture', 'Distal point selection, treating conditions away from the affected area, avoiding local sensitivity and inflammation', 'Superior effectiveness for chronic pain, musculoskeletal disorders, joint issues, internal organ dysfunctions, neurological conditions, infertility, and skin disorders', 'Enhanced safety profile as points are located on extremities rather than the body trunk', 'Systematic approach treating both root causes and immediate symptoms simultaneously']
  }, {
    id: 'balance-method',
    icon: BrainIcon,
    title: 'Dr. Tan Balance Method',
    color: 'teal',
    description: 'An innovative acupuncture system that simplifies complex TCM principles into practical, results-driven treatment protocols.',
    benefits: ['Immediate clinical effectiveness with rapid pain relief during treatment sessions', 'Balancing body energy (Qi) through interconnected meridian relationships using "mirroring" or "opposite" techniques', 'Distal point selection far from the pain site, making treatment comfortable and non-invasive', 'Five systematic approaches to meridian diagnosis and treatment selection', 'Minimal needles with maximum effectiveness for pain management, musculoskeletal issues, internal disorders, and functional conditions', 'Grounded in classical texts including the Neijing and I Ching while offering streamlined modern application']
  }, {
    id: 'scalp-acupuncture',
    icon: BrainIcon,
    title: 'Zhu Scalp Acupuncture',
    color: 'purple',
    description: 'Specialized technique targeting specific zones on the scalp to treat neurological conditions, cognitive disorders, and brain-related dysfunctions.',
    benefits: ['Stroke rehabilitation and paralysis recovery, with research showing 95% effectiveness in improving stroke-related paralysis', 'Autism spectrum disorders, ADHD, and developmental delays', "Cerebral palsy, multiple sclerosis, and Parkinson's disease", 'Cognitive enhancement and memory improvement', 'Headaches, migraines, and neurological pain syndromes', 'Research demonstrates scalp acupuncture stimulates brain tissue repair and retrains unaffected areas to compensate for damaged tissue']
  }, {
    id: 'cosmetic',
    icon: SparklesIcon,
    title: 'Cosmetic Acupuncture',
    color: 'pink',
    description: 'Non-surgical aesthetic treatment using acupuncture to enhance facial appearance, reduce aging signs, and promote radiant skin health.',
    benefits: ['Improved skin elasticity and firmness through collagen and elastin stimulation', 'Reduction of fine lines, wrinkles, and nasolabial folds', 'Enhanced facial muscle tone and lift without surgery', 'Improved blood circulation and lymphatic drainage', 'Reduction in pore size, facial flushing, and skin discoloration', 'Balancing of facial muscle tension for natural, youthful appearance', 'Holistic approach addressing internal organ health to manifest external beauty']
  }, {
    id: 'korean-cosmetic',
    icon: SparklesIcon,
    title: 'Korean Cosmetic Acupuncture',
    color: 'rose',
    description: 'Advanced facial acupuncture system combining three therapeutic principles: orthodox TCM with Qi modulation, local tissue stimulation through needle insertion, and unique Korean techniques controlling facial muscle tension.',
    benefits: ['Targets specific facial zones including wrinkles, sagging areas, and aging indicators', 'Incorporates manual acupuncture for cell regeneration (Jae-Seng technique) creating micro-channels that stimulate collagen synthesis', 'Regulates facial muscle groups including levator labii, zygomaticus muscles, and orbicularis oculi', 'Stimulates specific meridian points to improve organ function affecting skin health', 'Provides improvements in elasticity, wrinkles, pores, and overall facial aesthetics with minimal downtime']
  }, {
    id: 'fertility',
    icon: HeartIcon,
    title: 'Fertility Acupuncture',
    color: 'green',
    description: 'Evidence-based treatment addressing both male and female infertility through hormonal regulation, reproductive organ optimization, and stress reduction.',
    benefits: ['Significantly increased pregnancy rates (22 times higher than no treatment in some studies)', 'Enhanced IVF success rates when acupuncture is performed before and after embryo transfer', 'Improved ovulation rates and endometrial thickness', 'Balanced reproductive hormones including FSH, LH, and estrogen', 'Enhanced sperm quality, motility, and concentration in male infertility', 'Effective for PCOS, endometriosis, tubal infertility, and ovulatory disorders', 'Reduced miscarriage rates and improved embryo implantation']
  }, {
    id: 'pain',
    icon: ThermometerIcon,
    title: 'Pain Management Acupuncture',
    color: 'red',
    description: 'Comprehensive pain relief protocols utilizing multiple acupuncture modalities for acute and chronic pain conditions.',
    benefits: ['Musculoskeletal pain including back pain, neck pain, shoulder pain, joint pain, and arthritis', 'Neuropathic pain including sciatica, carpal tunnel syndrome, and peripheral neuropathy', 'Headaches and migraines with research showing 50-59% of patients experience 50% reduction in frequency lasting 6+ months', 'Post-surgical pain and injury recovery', 'Fibromyalgia and complex regional pain syndrome', 'Pain relief mechanisms include endorphin release, nervous system modulation, inflammation reduction, and improved circulation']
  }];
  const conditionCategories = [{
    category: 'Musculoskeletal & Pain Disorders',
    icon: ActivityIcon,
    conditions: [{
      name: 'Back Pain (Acute & Chronic)',
      description: 'Comprehensive treatment using Master Tung points and Dr. Tan Balance Method for rapid pain relief, muscle relaxation, and structural realignment'
    }, {
      name: 'Cervical Spondylosis',
      description: 'Targeted protocols addressing neck stiffness, pain, and restricted movement'
    }, {
      name: 'Slip Disc (Herniated Disc)',
      description: 'Conservative management promoting disc healing and reducing inflammation'
    }, {
      name: 'Sciatica',
      description: 'Effective treatment for sciatic nerve pain and leg numbness'
    }, {
      name: 'Knee Pain',
      description: 'Treatment for osteoarthritis, ligament injuries, and chronic knee instability'
    }, {
      name: 'Shoulder Pain',
      description: 'Comprehensive protocols for frozen shoulder and rotator cuff injuries'
    }, {
      name: 'Arthritis',
      description: 'Anti-inflammatory treatment reducing joint swelling and improving mobility'
    }, {
      name: 'Gout',
      description: 'Acupuncture combined with dietary guidance to reduce uric acid levels'
    }]
  }, {
    category: 'Neurological Conditions',
    icon: BrainIcon,
    conditions: [{
      name: 'Paralysis (Hemiplegia)',
      description: 'Scalp acupuncture promoting neural circuit remodeling with 95% effectiveness rate'
    }, {
      name: 'Post-Stroke Rehabilitation',
      description: 'Comprehensive program improving mobility and overall independence'
    }, {
      name: 'Migraine & Headaches',
      description: 'Treatment reducing migraine frequency by 50% or more in up to 59% of patients'
    }, {
      name: 'Epilepsy',
      description: 'Adjunctive treatment helping to reduce seizure frequency'
    }, {
      name: 'Tremors & Movement Disorders',
      description: "Supportive care for Parkinson's disease and essential tremor"
    }]
  }, {
    category: 'Metabolic & Endocrine Disorders',
    icon: ThermometerIcon,
    conditions: [{
      name: 'Type 2 Diabetes',
      description: 'Treatment improving blood sugar control and enhancing insulin sensitivity'
    }, {
      name: "Hashimoto's Thyroiditis",
      description: 'Significantly reducing antibody levels while regulating thyroid hormones'
    }, {
      name: 'Hypothyroidism',
      description: 'Improving thyroid hormone levels and alleviating symptoms'
    }, {
      name: 'Obesity & Weight Management',
      description: 'Reducing body weight and BMI through metabolic enhancement'
    }, {
      name: 'High Cholesterol',
      description: 'Treatment reducing total cholesterol and triglycerides'
    }]
  }, {
    category: 'Respiratory Conditions',
    icon: ActivityIcon,
    conditions: [{
      name: 'Asthma',
      description: 'Reducing bronchial inflammation and improving lung capacity'
    }, {
      name: 'Allergic Rhinitis',
      description: 'Effective relief from nasal congestion and inflammation'
    }, {
      name: 'Sinusitis',
      description: 'Promoting sinus drainage and alleviating facial pain'
    }, {
      name: 'Allergies',
      description: 'Immune system regulation reducing allergic responses'
    }]
  }, {
    category: 'Digestive Disorders',
    icon: HeartIcon,
    conditions: [{
      name: 'Constipation',
      description: 'Promoting intestinal motility and regulating digestive function'
    }, {
      name: 'GERD & Acidity',
      description: 'Relief from acid reflux and heartburn'
    }, {
      name: 'Gas & Bloating',
      description: 'Addressing abdominal distension and digestive discomfort'
    }, {
      name: 'Nausea & Vomiting',
      description: 'Effective antiemetic treatment'
    }]
  }, {
    category: 'Reproductive Health',
    icon: HeartIcon,
    conditions: [{
      name: 'Menstrual Disorders',
      description: 'Treatment for irregular periods, painful menstruation, and PMS'
    }, {
      name: 'PCOD/PCOS',
      description: 'Improving ovulation and hormonal balance'
    }, {
      name: 'Female Infertility',
      description: 'Evidence-based fertility enhancement'
    }, {
      name: 'Male Infertility',
      description: 'Enhancing sperm quality and reproductive health'
    }, {
      name: 'Erectile Dysfunction',
      description: 'Improving blood flow and sexual function'
    }, {
      name: 'Menopause Symptoms',
      description: 'Relief from hot flashes and hormonal imbalances'
    }]
  }, {
    category: 'Mental Health & Sleep',
    icon: BrainIcon,
    conditions: [{
      name: 'Depression',
      description: 'Promoting neurotransmitter balance and mood elevation'
    }, {
      name: 'Anxiety & Stress',
      description: 'Effective stress reduction and relaxation'
    }, {
      name: 'Insomnia',
      description: 'Improving sleep quality and duration'
    }]
  }, {
    category: 'Other Conditions',
    icon: EyeIcon,
    conditions: [{
      name: 'Eye Diseases',
      description: 'Treatment for dry eyes, eye strain, and vision decline'
    }, {
      name: 'Kidney Stones',
      description: 'Promoting stone passage and pain reduction'
    }, {
      name: 'Prostate Disorders',
      description: 'Relief from BPH symptoms'
    }, {
      name: 'Height Increase',
      description: 'Growth optimization in children and adolescents'
    }]
  }];
  return <div className="w-full bg-white" data-id="element-955">
      <section className="bg-gradient-to-br from-blue-50 to-teal-50 py-16 px-4 w-full" data-id="element-956">
        <div className="max-w-7xl mx-auto text-center" data-id="element-957">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-958">
            Treatment Modalities & Specializations
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-959">
            Advanced acupuncture techniques and comprehensive treatment
            protocols for a wide range of health conditions
          </p>
        </div>
      </section>
      <section className="py-16 px-4 w-full" data-id="element-960">
        <div className="max-w-7xl mx-auto" data-id="element-961">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-962">
            Specialized Treatment Modalities
          </h2>
          <div className="space-y-6" data-id="element-963">
            {specializations.map(spec => <div key={spec.id} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-964">
                <button onClick={() => toggleSection(spec.id)} className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-965">
                  <div className="flex items-center space-x-4" data-id="element-966">
                    <div className={`w-12 h-12 bg-${spec.color}-100 rounded-lg flex items-center justify-center`} data-id="element-967">
                      <spec.icon className={`w-6 h-6 text-${spec.color}-600`} data-id="element-968" />
                    </div>
                    <div className="text-left" data-id="element-969">
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-970">
                        {spec.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-971">
                        {spec.description}
                      </p>
                    </div>
                  </div>
                  {expandedSection === spec.id ? <ChevronUpIcon className="w-6 h-6 text-gray-400" data-id="element-972" /> : <ChevronDownIcon className="w-6 h-6 text-gray-400" data-id="element-973" />}
                </button>
                {expandedSection === spec.id && <div className="px-6 pb-6" data-id="element-974">
                    <div className="pl-16 space-y-3" data-id="element-975">
                      {spec.benefits.map((benefit, index) => <div key={index} className="flex items-start" data-id="element-976">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0" data-id="element-977"></div>
                          <p className="text-gray-700 text-sm" data-id="element-978">{benefit}</p>
                        </div>)}
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>
      </section>
      <section className="py-16 px-4 bg-gray-50 w-full" data-id="element-979">
        <div className="max-w-7xl mx-auto" data-id="element-980">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-981">
            Conditions We Treat
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-982">
            {conditionCategories.map((category, catIndex) => <div key={catIndex} className="bg-white p-6 rounded-xl shadow-lg" data-id="element-983">
                <div className="flex items-center mb-4" data-id="element-984">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3" data-id="element-985">
                    <category.icon className="w-5 h-5 text-blue-600" data-id="element-986" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900" data-id="element-987">
                    {category.category}
                  </h3>
                </div>
                <div className="space-y-4" data-id="element-988">
                  {category.conditions.map((condition, condIndex) => <div key={condIndex} className="border-l-2 border-blue-200 pl-4" data-id="element-989">
                      <h4 className="font-semibold text-gray-900 text-sm" data-id="element-990">
                        {condition.name}
                      </h4>
                      <p className="text-gray-600 text-xs mt-1" data-id="element-991">
                        {condition.description}
                      </p>
                    </div>)}
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}